import { createStore } from './config';

export type TDashboardMockModeScope = 'performance' | 'consumption';

interface DashboardMockModeState {
  performanceMockMode: boolean;
  consumptionMockMode: boolean;
  setMockMode: (scope: TDashboardMockModeScope, enabled: boolean) => void;
}

const STORAGE_KEYS: Record<TDashboardMockModeScope, string> = {
  performance: 'dashboard-performance-mock-mode',
  consumption: 'dashboard-consumption-mock-mode',
};

const readInitial = (scope: TDashboardMockModeScope): boolean => {
  try {
    return localStorage.getItem(STORAGE_KEYS[scope]) === 'true';
  } catch {
    return false;
  }
};

const writeStorage = (scope: TDashboardMockModeScope, enabled: boolean) => {
  try {
    localStorage.setItem(STORAGE_KEYS[scope], String(enabled));
  } catch {
    // Storage failures are non-fatal: the toggle just won't persist this session.
  }
};

export const useDashboardMockModeStore = createStore<DashboardMockModeState>(
  'dashboard-mock-mode-store',
  (_, get, api) => ({
    performanceMockMode: readInitial('performance'),
    consumptionMockMode: readInitial('consumption'),

    setMockMode: (scope, enabled) => {
      writeStorage(scope, enabled);
      const key =
        scope === 'performance' ? 'performanceMockMode' : 'consumptionMockMode';
      if (get()[key] === enabled) return;
      api.setState(
        { [key]: enabled } as Partial<DashboardMockModeState>,
        false,
        {
          type: 'dashboardMockMode/setMockMode',
          scope,
          enabled,
        },
      );
    },
  }),
);

export const selectPerformanceMockMode = (state: DashboardMockModeState) =>
  state.performanceMockMode;
export const selectConsumptionMockMode = (state: DashboardMockModeState) =>
  state.consumptionMockMode;
export const selectSetDashboardMockMode = (state: DashboardMockModeState) =>
  state.setMockMode;
